import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ReactElement, lazy, Suspense, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeInit } from "@/shared/layout/ThemeInit";
import ErrorBoundary from "@/shared/layout/ErrorBoundary";
import AppShell from "@/shared/layout/AppShell";
import { isAdminUser } from "@/shared/rbac";
import { Sprout } from "lucide-react";

import AuthPage from "@/pages/AuthPage";
import Paywall from "@/pages/Paywall";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";
import FarmersLanding from "@/pages/FarmersLanding";
import DemoPreview from "@/pages/DemoPreview";
import Blog from "@/pages/Blog";
import Calculators from "@/pages/Calculators";
import Compare from "@/pages/Compare";
import VerticalLanding from "@/pages/VerticalLanding";
import PaymentReturn from "@/pages/PaymentReturn";

// Lazy-loaded routes — split each feature into its own chunk for faster initial load.
const Overview = lazy(() => import("@/pages/Overview"));
const ExecutiveDashboard = lazy(() => import("@/features/executive/ExecutiveDashboard"));
const ForecastPage = lazy(() => import("@/features/forecast/ForecastPage"));
const StoreClusters = lazy(() => import("@/features/clusters/StoreClusters"));
const Categories = lazy(() => import("@/pages/Categories"));
const Services = lazy(() => import("@/pages/Services"));
const Optimize = lazy(() => import("@/pages/Optimize"));
const AssortmentEngine = lazy(() => import("@/features/assortment/AssortmentEngine"));
const ShelfOptimizer = lazy(() => import("@/features/shelf/ShelfOptimizer"));
const OpportunityFinder = lazy(() => import("@/features/opportunities/OpportunityFinder"));
const Copilot = lazy(() => import("@/features/copilot/Copilot"));
const FinancialSimulation = lazy(() => import("@/features/simulation/FinancialSimulation"));
const DigitalTwin = lazy(() => import("@/features/twin/DigitalTwin"));
const LiveSales = lazy(() => import("@/features/integrations/LiveSales"));
const Upgrade = lazy(() => import("@/pages/Upgrade"));
const WhatIf = lazy(() => import("@/pages/WhatIf"));
const ScenarioHistory = lazy(() => import("@/features/scenarios/ScenarioHistory"));
const Trends = lazy(() => import("@/pages/Trends"));
const Stores = lazy(() => import("@/pages/Stores"));
const Competitors = lazy(() => import("@/pages/Competitors"));
const DataReports = lazy(() => import("@/pages/DataReports"));
const SettingsPage = lazy(() => import("@/features/settings/SettingsPage"));
const Help = lazy(() => import("@/pages/Help"));
const UsersPage = lazy(() => import("@/features/users/UsersPage"));
const AuditPage = lazy(() => import("@/features/audit/AuditPage"));
const OutreachPage = lazy(() => import("@/features/outreach/OutreachPage"));
const NotificationsPage = lazy(() => import("@/features/notifications/NotificationsPage"));

function RouteFallback() {
  return (
    <div className="flex items-center justify-center py-24" data-testid="route-loading">
      <div className="h-8 w-8 rounded-full border-2 border-muted border-t-ai animate-spin" />
    </div>
  );
}

function BootSpinner() {
  return (
    <div className="min-h-screen bg-forest-900 text-white flex flex-col items-center justify-center gap-4">
      <div className="h-11 w-11 rounded-md bg-ai flex items-center justify-center animate-pulse">
        <Sprout className="h-6 w-6" strokeWidth={2.4} />
      </div>
      <div className="h-6 w-6 rounded-full border-2 border-white/30 border-t-ai animate-spin" />
    </div>
  );
}

function DataReportsRoute() {
  const qc = useQueryClient();
  return <DataReports onDataChanged={() => qc.invalidateQueries()} />;
}

function RequireAdmin({ children }: { children: ReactElement }) {
  const { user } = useAuth();
  return isAdminUser(user) ? children : <Navigate to="/overview" replace />;
}

function RequireFeature({ feature, children }: { feature: string; children: ReactElement }) {
  const { user } = useAuth();
  const feats = new Set<string>((user?.features as string[]) || []);
  return feats.has(feature) ? children : <Navigate to={`/upgrade?feature=${feature}`} replace />;
}

function Workspace() {
  return (
    <AppShell>
      <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/overview" element={<Overview />} />
            <Route path="/executive" element={<ExecutiveDashboard />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/services" element={<Services />} />
            <Route path="/optimize" element={<Optimize />} />
            <Route path="/assortment" element={<AssortmentEngine />} />
            <Route path="/forecast" element={<RequireFeature feature="forecast"><ForecastPage /></RequireFeature>} />
            <Route path="/whatif" element={<RequireFeature feature="whatif"><WhatIf /></RequireFeature>} />
            <Route path="/trends" element={<Trends />} />
            <Route path="/stores" element={<Stores />} />
            <Route path="/clusters" element={<RequireFeature feature="clusters"><StoreClusters /></RequireFeature>} />
            <Route path="/shelf" element={<RequireFeature feature="shelf"><ShelfOptimizer /></RequireFeature>} />
            <Route path="/opportunities" element={<OpportunityFinder />} />
            <Route path="/copilot" element={<Copilot />} />
            <Route path="/simulation" element={<RequireFeature feature="simulation"><FinancialSimulation /></RequireFeature>} />
            <Route path="/twin" element={<RequireFeature feature="digital_twin"><DigitalTwin /></RequireFeature>} />
            <Route path="/live-sales" element={<RequireFeature feature="pos_ingest"><LiveSales /></RequireFeature>} />
            <Route path="/competitors" element={<Competitors />} />
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/data" element={<DataReportsRoute />} />
            <Route path="/scenarios" element={<ScenarioHistory />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/help" element={<Help />} />
            <Route path="/users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />
            <Route path="/audit" element={<RequireAdmin><AuditPage /></RequireAdmin>} />
            <Route path="/outreach" element={<RequireAdmin><OutreachPage /></RequireAdmin>} />
            <Route path="*" element={<Navigate to="/overview" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </AppShell>
  );
}

function Gate() {
  const { user } = useAuth();
  const [authMode, setAuthMode] = useState<false | "register" | "login">(false);
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");
  const resetToken = params.get("reset_token");

  if (resetToken) return <ResetPassword token={resetToken} />;
  if (user === null) return <BootSpinner />;
  if (user === false) {
    if (authMode) return <AuthPage initialMode={authMode} />;
    const goHome = () => { window.location.href = "/"; };
    if (window.location.pathname.startsWith("/blog")) {
      const parts = window.location.pathname.split("/").filter(Boolean);
      const slug = parts[1] || null;
      return <Blog slug={slug} onGetStarted={() => setAuthMode("register")} onHome={goHome} />;
    }
    if (window.location.pathname.startsWith("/tools")) {
      const parts = window.location.pathname.split("/").filter(Boolean);
      const slug = parts[1] || null;
      return <Calculators slug={slug} onGetStarted={() => setAuthMode("register")} onHome={goHome} />;
    }
    if (window.location.pathname.startsWith("/compare")) {
      const parts = window.location.pathname.split("/").filter(Boolean);
      const slug = parts[1] || null;
      return <Compare slug={slug} onGetStarted={() => setAuthMode("register")} onHome={goHome} />;
    }
    if (window.location.pathname.startsWith("/demo")) {
      const parts = window.location.pathname.split("/").filter(Boolean);
      const v = parts[1] || "grocery";
      return <DemoPreview initialVertical={v} onGetStarted={() => setAuthMode("register")} onLogin={() => setAuthMode("login")} onHome={goHome} />;
    }
    if (window.location.pathname === "/farmers")
      return <FarmersLanding onGetStarted={() => setAuthMode("register")} onLogin={() => setAuthMode("login")} onHome={goHome} />;
    {
      const vpath = window.location.pathname.replace(/\/+$/, "");
      if (vpath === "/electronics" || vpath === "/appliances" || vpath === "/cellular" || vpath === "/retail")
        return <VerticalLanding vertical={vpath.slice(1)} onGetStarted={() => setAuthMode("register")} onLogin={() => setAuthMode("login")} onHome={goHome} />;
    }
    return <Landing onGetStarted={() => setAuthMode("register")} onLogin={() => setAuthMode("login")} />;
  }
  if (sessionId) return <PaymentReturn sessionId={sessionId} />;
  if (!user.has_access) return <Paywall />;
  return <Workspace />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeInit />
        <BrowserRouter>
          <Gate />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
